// TelcoTrack — Navolávání calendar: person pairing, Google event → building, search, polygons.

import { foldFilterSearchText } from './filter-option-search.util';
import { NAVOLAVANI_UNASSIGNED_POLYGON_SLUG } from '../constants/navolavani.constants';
import {
  isHouseNumberMapSearchToken,
  matchesMapSearchTokens,
  normalizeMapSearchQuery,
  tokenizeMapSearchQuery,
} from './map-search-query.util';

export interface NavolavaniRegisteredPerson {
  userId: string;
  email: string;
  name: string;
  /** Optional HR phone from `users.phone` (calendar detail). */
  phone?: string | null;
}

export interface NavolavaniCalendarPersonPairInput {
  calendarId: string;
  technicianEmail?: string | null;
  technicianUserId?: string | null;
}

export interface NavolavaniBuildingLocationMatchInput {
  objectId: string;
  address?: string | null;
  name?: string | null;
  polygonId?: string | null;
  polygonName?: string | null;
  polygonKia?: string | null;
}

export interface NavolavaniCalendarSearchEvent {
  summary?: string | null;
  address?: string | null;
  location?: string | null;
  note?: string | null;
  technicianName?: string | null;
  technicianEmail?: string | null;
  technicianPhone?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  polygonName?: string | null;
  polygonTitle?: string | null;
  calendarTitle?: string | null;
}

const COUNTRY_NOISE = new Set(['cz', 'czechia', 'cesko', 'czech', 'republic', 'ceska', 'republika']);
const TITLE_NOISE = new Set(['zavrt', 'navolavani', 'schuzka', 'obhlidka']);

function normalizeEmail(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function emailsFromCalendar(calendar: NavolavaniCalendarPersonPairInput): string[] {
  const out: string[] = [];
  const tech = normalizeEmail(calendar.technicianEmail);
  if (tech) out.push(tech);
  const calendarId = (calendar.calendarId ?? '').trim().toLowerCase();
  if (calendarId.includes('@')) out.push(calendarId);
  return [...new Set(out)];
}

/**
 * Fail-closed: calendar ↔ registered TelcoTrack person by userId or exact email
 * (`technicianEmail` / calendar id). No fuzzy name match.
 */
export function pairNavolavaniCalendarToPerson(
  calendar: NavolavaniCalendarPersonPairInput,
  people: ReadonlyArray<NavolavaniRegisteredPerson>,
): NavolavaniRegisteredPerson | null {
  if (!people.length) return null;
  const byId = new Map(people.map((person) => [person.userId, person]));
  const byEmail = new Map(people.map((person) => [normalizeEmail(person.email), person]));
  const userId = (calendar.technicianUserId ?? '').trim();
  if (userId) {
    const hit = byId.get(userId);
    if (hit) return hit;
  }
  for (const email of emailsFromCalendar(calendar)) {
    const hit = byEmail.get(email);
    if (hit) return hit;
  }
  return null;
}

export interface NavolavaniConnectedCalendarInput extends NavolavaniCalendarPersonPairInput {
  enabled?: boolean;
}

/**
 * People paired to **imported / enabled** Google calendars (`enabled=true`).
 * Used for calendar layer chips / pairing — **not** the Navolávání technician picker
 * (that uses map-edit assignees via `hasWorkReservationAssigneeRole`).
 */
export function connectedNavolavaniTechnicians(
  calendars: ReadonlyArray<NavolavaniConnectedCalendarInput>,
  people: ReadonlyArray<NavolavaniRegisteredPerson>,
): NavolavaniRegisteredPerson[] {
  const byId = new Map<string, NavolavaniRegisteredPerson>();
  for (const row of calendars) {
    if (row.enabled !== true) continue;
    const person = pairNavolavaniCalendarToPerson(row, people);
    if (!person?.userId || !person.email) continue;
    byId.set(person.userId, person);
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, 'cs'));
}

export function navolavaniCalendarPersonLabel(
  calendar: NavolavaniCalendarPersonPairInput & { summary?: string | null },
  person: NavolavaniRegisteredPerson | null,
): string {
  const name = (person?.name ?? '').trim();
  if (name) return name;
  const summary = (calendar.summary ?? '').trim();
  if (summary) return summary;
  const email = normalizeEmail(calendar.technicianEmail);
  if (email) return email;
  return (calendar.calendarId ?? '').trim() || 'Kalendář';
}

interface AddressMatchParts {
  objectId: string;
  building: NavolavaniBuildingLocationMatchInput;
  foldedAddress: string;
  street: string[];
  house: string[];
  city: string[];
  streetKey: string;
  houseKey: string;
}

function isPscPair(token: string, next: string | undefined): boolean {
  return /^\d{3}$/.test(token) && !!next && /^\d{2}$/.test(next);
}

function isFiveDigitPsc(token: string): boolean {
  return /^\d{5}$/.test(token);
}

function parseAddressParts(building: NavolavaniBuildingLocationMatchInput): AddressMatchParts | null {
  const foldedAddress = normalizeMapSearchQuery(building.address ?? '');
  const rawTokens = tokenizeMapSearchQuery(building.address ?? '');
  const house: string[] = [];
  const place: string[] = [];
  for (let i = 0; i < rawTokens.length; i += 1) {
    const token = rawTokens[i] ?? '';
    const next = rawTokens[i + 1];
    if (!token || COUNTRY_NOISE.has(token) || TITLE_NOISE.has(token)) continue;
    if (isPscPair(token, next)) {
      i += 1;
      continue;
    }
    if (isFiveDigitPsc(token)) continue;
    if (isHouseNumberMapSearchToken(token)) {
      house.push(token);
      continue;
    }
    place.push(token);
  }
  let street = place;
  let city: string[] = [];
  if (place.length >= 2) {
    city = [place[place.length - 1] as string];
    street = place.slice(0, -1);
  }
  if (!foldedAddress && !street.length && !house.length) return null;
  return {
    objectId: building.objectId,
    building,
    foldedAddress,
    street,
    house,
    city,
    streetKey: street.join(' '),
    houseKey: house.join(' '),
  };
}

function eventMatchHaystack(event: {
  location?: string | null;
  summary?: string | null;
  description?: string | null;
  note?: string | null;
  address?: string | null;
}): string {
  return normalizeMapSearchQuery(
    [event.location, event.summary, event.description, event.note, event.address]
      .filter((part) => (part ?? '').trim())
      .join(' '),
  );
}

function eventHasTokens(hay: string, tokens: readonly string[]): boolean {
  return tokens.length > 0 && matchesMapSearchTokens(hay, [...tokens]);
}

function houseCompatible(hay: string, house: readonly string[]): boolean {
  if (!house.length) return false;
  const hayTokens = hay.split(/\s+/).filter(Boolean);
  return house.every((token) => {
    if (matchesMapSearchTokens(hay, [token])) return true;
    const slash = token.indexOf('/');
    if (slash < 0) return false;
    const base = token.slice(0, slash);
    return isHouseNumberMapSearchToken(base) && hayTokens.includes(base);
  });
}

function uniqueOrNull<T>(items: T[]): T | null {
  return items.length === 1 ? (items[0] ?? null) : null;
}

function disambiguateByContext(
  hits: AddressMatchParts[],
  hay: string,
): AddressMatchParts | null {
  if (hits.length <= 1) return uniqueOrNull(hits);
  const withCity = hits.filter((hit) => hit.city.length && eventHasTokens(hay, hit.city));
  if (withCity.length === 1) return withCity[0] ?? null;
  const withKia = hits.filter((hit) => {
    const kia = normalizeMapSearchQuery(hit.building.polygonKia ?? '');
    return kia.length >= 3 && hay.includes(kia);
  });
  if (withKia.length === 1) return withKia[0] ?? null;
  const withPolygon = hits.filter((hit) => {
    const name = normalizeMapSearchQuery(hit.building.polygonName ?? '');
    return name.length >= 4 && hay.includes(name);
  });
  if (withPolygon.length === 1) return withPolygon[0] ?? null;
  return null;
}

/**
 * Unique building for a Google event (location, summary, description).
 * Empty location still matches `Závrt · adresa` / street+house in the title.
 * Ambiguous hits (several buildings) stay unpaired.
 */
export function matchNavolavaniBuildingToLocation(
  event: {
    location?: string | null;
    summary?: string | null;
    description?: string | null;
    note?: string | null;
    address?: string | null;
  },
  buildings: ReadonlyArray<NavolavaniBuildingLocationMatchInput>,
): NavolavaniBuildingLocationMatchInput | null {
  const hay = eventMatchHaystack(event);
  if (!hay) return null;
  const parsed = buildings
    .map((building) => parseAddressParts(building))
    .filter((row): row is AddressMatchParts => row != null);

  const fullHits = parsed.filter(
    (row) => row.foldedAddress.length >= 6 && hay.includes(row.foldedAddress),
  );
  const full = disambiguateByContext(fullHits, hay);
  if (full) return full.building;

  const streetHouseHits = parsed.filter(
    (row) => row.street.length > 0 && row.house.length > 0 && eventHasTokens(hay, row.street) && houseCompatible(hay, row.house),
  );
  const streetHouse = disambiguateByContext(streetHouseHits, hay);
  if (streetHouse) return streetHouse.building;

  const eventHasAnyHouse = hay.split(/\s+/).some((token) => isHouseNumberMapSearchToken(token));
  if (!eventHasAnyHouse) {
    const streetOnly = parsed.filter(
      (row) => row.street.length > 0 && eventHasTokens(hay, row.street),
    );
    const uniqueStreet = streetOnly.filter((row) => {
      const sameStreet = parsed.filter((other) => other.streetKey === row.streetKey);
      return sameStreet.length === 1;
    });
    const streetHit = disambiguateByContext(uniqueStreet, hay);
    if (streetHit) return streetHit.building;
  }

  const kiaHits = parsed.filter((row) => {
    const kia = normalizeMapSearchQuery(row.building.polygonKia ?? '');
    return kia.length >= 3 && hay.includes(kia);
  });
  const uniqueKia = kiaHits.filter((row) => {
    const kia = normalizeMapSearchQuery(row.building.polygonKia ?? '');
    return parsed.filter((other) => normalizeMapSearchQuery(other.building.polygonKia ?? '') === kia).length === 1;
  });
  if (uniqueKia.length === 1) return uniqueKia[0]?.building ?? null;

  const nameHits = parsed.filter((row) => {
    const name = normalizeMapSearchQuery(row.building.name ?? '');
    return name.length >= 6 && hay.includes(name);
  });
  if (nameHits.length === 1) return nameHits[0]?.building ?? null;

  return null;
}

/** Client-side event search (address, person, polygon/KIA, note, calendar title). Empty query = all. */
export function navolavaniCalendarEventMatchesSearch(
  event: NavolavaniCalendarSearchEvent,
  query: string,
): boolean {
  const q = foldFilterSearchText(query);
  if (!q) return true;
  const hay = foldFilterSearchText(
    [
      event.summary,
      event.address,
      event.location,
      event.note,
      event.technicianName,
      event.technicianEmail,
      event.technicianPhone,
      event.contactName,
      event.contactPhone,
      event.contactEmail,
      event.polygonName,
      event.polygonTitle,
      event.calendarTitle,
    ]
      .filter(Boolean)
      .join(' '),
  );
  return hay.includes(q);
}

/** Empty selection = all polygons. Unassigned events use `bez-polygonu`. */
export function navolavaniCalendarEventMatchesPolygons(
  polygonId: string | null | undefined,
  selected: readonly string[],
  unassignedSlug: string = NAVOLAVANI_UNASSIGNED_POLYGON_SLUG,
): boolean {
  if (!selected.length) return true;
  const id = (polygonId ?? '').trim() || unassignedSlug;
  return selected.includes(id);
}
